import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, User, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import api, { formatPrice } from '@/lib/api';
import NotificationBell from '@/components/NotificationBell';

export default function Header() {
    const { user } = useAuth();
    const { cartCount, cart, cartOpen, setCartOpen, updateCart, removeFromCart } = useCart();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [topBar, setTopBar] = useState({ left: 'Free delivery on orders above \u20A650,000 within Abuja', phone: '07050657701' });
    const [categories, setCategories] = useState([]);
    const [shopOpen, setShopOpen] = useState(false);
    const navigate = useNavigate();
    const searchRef = useRef(null);
    const shopRef = useRef(null);

    useEffect(() => {
        api.get('/settings').then(({ data }) => {
            setTopBar({
                left: data.free_delivery_text || 'Free delivery on orders above \u20A650,000 within Abuja',
                phone: data.phone_number || data.whatsapp_number || '07050657701',
            });
        }).catch(() => {});
        api.get('/categories').then(({ data }) => {
            setCategories(Array.isArray(data) ? data : []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (e) => { if (shopRef.current && !shopRef.current.contains(e.target)) setShopOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchQuery.length >= 2) {
                try {
                    const { data } = await api.get(`/search?q=${encodeURIComponent(searchQuery)}`);
                    setSearchResults(data);
                    setShowSearch(true);
                } catch { setSearchResults([]); }
            } else {
                setSearchResults([]);
                setShowSearch(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery) { navigate(`/shop?search=${encodeURIComponent(searchQuery)}`); setShowSearch(false); setSearchQuery(''); }
    };

    return (
        <>
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="main-header">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Top bar */}
                    <div className="hidden md:flex items-center justify-between py-1.5 text-xs text-gray-500 border-b border-gray-50">
                        <span className="font-body" data-testid="header-free-delivery">{topBar.left}</span>
                        <span className="font-body">Call us: <a href={`tel:${topBar.phone}`} className="hover:text-pink-600" data-testid="header-phone">{topBar.phone}</a></span>
                    </div>
                    {/* Main nav */}
                    <div className="flex items-center justify-between h-16 gap-4">
                        <Link to="/" className="flex-shrink-0" data-testid="logo-link">
                            <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
                                Beauty<span className="text-pink-600">By</span>Chancy
                            </h1>
                        </Link>
                        {/* Search */}
                        <div className="hidden md:block flex-1 max-w-lg mx-8 relative" ref={searchRef}>
                            <form onSubmit={handleSearch} className="relative">
                                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search products..." data-testid="search-input"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-full pl-10 pr-4 py-2.5 text-sm font-body focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all" />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            </form>
                            {showSearch && searchResults.length > 0 && (
                                <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 max-h-80 overflow-y-auto z-50" data-testid="search-results">
                                    {searchResults.map(p => (
                                        <Link key={p.id} to={`/product/${p.slug}`} onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                            className="flex items-center gap-3 p-3 hover:bg-pink-50 transition-colors">
                                            {p.images?.[0] && <img src={p.images[0]} alt={p.name} loading="lazy" decoding="async" className="w-10 h-10 rounded-lg object-cover" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate font-body">{p.name}</p>
                                                <p className="text-sm text-pink-600 font-semibold font-body">{formatPrice(p.sale_price || p.price)}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Nav links */}
                        <nav className="hidden lg:flex items-center gap-6">
                            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-pink-600 transition-colors font-body" data-testid="nav-home">Home</Link>
                            <div
                                className="relative"
                                ref={shopRef}
                                onMouseEnter={() => setShopOpen(true)}
                                onMouseLeave={() => setShopOpen(false)}
                            >
                                <button
                                    type="button"
                                    onClick={() => { setShopOpen(s => !s); }}
                                    className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-pink-600 transition-colors font-body"
                                    data-testid="nav-shop"
                                    aria-haspopup="true"
                                    aria-expanded={shopOpen}
                                >
                                    Shop <ChevronDown className={`w-3.5 h-3.5 transition-transform ${shopOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {shopOpen && (
                                    <div
                                        className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-64 z-50"
                                        data-testid="shop-dropdown"
                                    >
                                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                            <Link
                                                to="/shop"
                                                onClick={() => setShopOpen(false)}
                                                className="block px-4 py-2.5 text-sm font-body font-semibold text-gray-900 hover:bg-pink-50 hover:text-pink-600 transition-colors border-b border-gray-100"
                                                data-testid="shop-dropdown-all"
                                            >
                                                Shop All
                                            </Link>
                                            <div className="max-h-80 overflow-y-auto py-1">
                                                {categories.length === 0 && (
                                                    <div className="px-4 py-3 text-xs font-body text-gray-400">Loading categories…</div>
                                                )}
                                                {categories.map(c => (
                                                    <Link
                                                        key={c.id}
                                                        to={`/shop/${c.slug}`}
                                                        onClick={() => setShopOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-pink-50 hover:text-pink-600 transition-colors"
                                                        data-testid={`shop-dropdown-cat-${c.slug}`}
                                                    >
                                                        {c.image && (
                                                            <img src={c.image} alt="" loading="lazy" decoding="async" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-100" />
                                                        )}
                                                        <span className="flex-1 truncate">{c.name}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </nav>
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCartOpen(true)} className="relative p-2 rounded-full hover:bg-pink-50 transition-colors" data-testid="cart-button">
                                <ShoppingCart className="w-5 h-5 text-gray-700" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-pink-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center cart-pulse" data-testid="cart-count">{cartCount}</span>
                                )}
                            </button>
                            {user && <NotificationBell testId="nav-notification-bell" />}
                            <Link to={user ? (user.role === 'admin' ? '/admin' : '/account') : '/login'} className="hidden md:flex p-2 rounded-full hover:bg-pink-50 transition-colors" data-testid="account-button">
                                <User className="w-5 h-5 text-gray-700" />
                            </Link>
                            <button onClick={() => setMobileMenuOpen(true)} className="md:hidden p-2 rounded-full hover:bg-pink-50" data-testid="mobile-menu-button">
                                <Menu className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>
                    </div>
                    {/* Mobile search */}
                    <div className="md:hidden pb-3" ref={searchRef}>
                        <form onSubmit={handleSearch} className="relative">
                            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Search products..." data-testid="mobile-search-input"
                                className="w-full bg-gray-50 border border-gray-200 rounded-full pl-10 pr-4 py-2.5 text-sm font-body focus:outline-none focus:ring-2 focus:ring-pink-500" />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </form>
                    </div>
                </div>
            </header>

            {/* Cart Drawer */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
                    <SheetHeader><SheetTitle className="font-heading text-xl">Shopping Cart ({cartCount})</SheetTitle></SheetHeader>
                    <div className="flex-1 overflow-y-auto py-4">
                        {cart.items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                <ShoppingCart className="w-16 h-16 mb-4" />
                                <p className="font-body text-lg">Your cart is empty</p>
                                <Button onClick={() => { setCartOpen(false); navigate('/shop'); }} className="mt-4 bg-pink-600 hover:bg-pink-700 rounded-full font-body" data-testid="continue-shopping-btn">Continue Shopping</Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {cart.items.map((item, idx) => (
                                    <div key={idx} className="flex gap-3 bg-gray-50 rounded-xl p-3" data-testid={`cart-item-${idx}`}>
                                        {item.image && <img src={item.image} alt={item.name} loading="lazy" decoding="async" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate font-body">{item.name}</p>
                                            <p className="text-sm text-pink-600 font-semibold font-body">{formatPrice(item.price)}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <button onClick={() => updateCart(item.product_id, item.quantity - 1, item.variant)}
                                                    className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-sm hover:bg-pink-50" data-testid={`cart-item-decrease-${idx}`}>-</button>
                                                <span className="text-sm font-medium font-body w-6 text-center">{item.quantity}</span>
                                                <button onClick={() => updateCart(item.product_id, item.quantity + 1, item.variant)}
                                                    className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-sm hover:bg-pink-50" data-testid={`cart-item-increase-${idx}`}>+</button>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(item.product_id, item.variant)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`cart-item-remove-${idx}`}>
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {cart.items.length > 0 && (
                        <div className="border-t pt-4 space-y-3">
                            <div className="flex justify-between text-lg font-semibold font-body">
                                <span>Subtotal</span><span className="text-pink-600">{formatPrice(cart.total)}</span>
                            </div>
                            <Button onClick={() => { setCartOpen(false); navigate('/checkout'); }}
                                className="w-full bg-pink-600 hover:bg-pink-700 rounded-full h-12 text-base font-body" data-testid="checkout-btn">
                                Proceed to Checkout
                            </Button>
                            <Button variant="outline" onClick={() => { setCartOpen(false); navigate('/cart'); }}
                                className="w-full rounded-full h-10 font-body border-pink-200 text-pink-700 hover:bg-pink-50" data-testid="view-cart-btn">
                                View Full Cart
                            </Button>
                            <Button variant="ghost" onClick={() => { setCartOpen(false); navigate('/shop'); }}
                                className="w-full rounded-full h-10 font-body text-gray-600 hover:text-pink-600 hover:bg-pink-50" data-testid="drawer-continue-shopping-btn">
                                ← Continue Shopping
                            </Button>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetContent side="left" className="w-72 overflow-y-auto">
                    <SheetHeader><SheetTitle className="font-heading text-xl">Menu</SheetTitle></SheetHeader>
                    <nav className="flex flex-col gap-1 mt-6">
                        {[{to:'/',label:'Home'},{to:'/shop',label:'Shop'},{to:'/cart',label:'Cart'},{to:user?(user.role==='admin'?'/admin':'/account'):'/login',label:user?'My Account':'Sign In'}]
                            .map(({to,label}) => (
                                <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)}
                                    className="py-3 px-4 text-gray-700 hover:bg-pink-50 hover:text-pink-600 rounded-lg transition-colors font-body text-base font-medium" data-testid={`mobile-menu-${label.toLowerCase().replace(' ','-')}`}>{label}</Link>
                            ))}
                    </nav>
                    {categories.length > 0 && (
                        <div className="mt-6 border-t pt-4">
                            <p className="text-[11px] font-body uppercase tracking-wider text-gray-400 mb-2 px-4">Shop by Category</p>
                            <div className="flex flex-col">
                                {categories.map(c => (
                                    <Link key={c.id} to={`/shop/${c.slug}`} onClick={() => setMobileMenuOpen(false)}
                                          className="flex items-center gap-3 py-2.5 px-4 text-gray-700 hover:bg-pink-50 hover:text-pink-600 rounded-lg transition-colors font-body text-sm" data-testid={`mobile-menu-cat-${c.slug}`}>
                                        {c.image && <img src={c.image} alt="" loading="lazy" decoding="async" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />}
                                        <span className="flex-1 truncate">{c.name}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </>
    );
}
